"use client";

/**
 * Location-Based Cityscape Background
 * Gets user's location, finds nearest metro, and displays that cityscape from S3
 * Falls back to default if location is unknown or images fail
 * Does NOT rotate - sets image once on page load
 *
 * Loading state: dark atmospheric gradient with subtle shimmer pulse
 * so the user never sees a white flash while geolocation resolves.
 */

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { getCityscapeForLocation } from "@/app/actions/s3-cityscape";
import { getUserGeolocation } from "@/lib/geolocation";

// Default fallback images (variety to avoid always showing Dallas)
const DEFAULT_FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1920&q=80", // Chicago
  "https://images.unsplash.com/photo-1496442226666-8d4a0e62e6e9?w=1920&q=80", // NYC
  "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=80", // London
  "https://images.unsplash.com/photo-1444723121867-7a241cacace9?w=1920&q=80", // Abstract city
];

function getRandomFallbackImage(): string {
  const index = Math.floor(Math.random() * DEFAULT_FALLBACK_IMAGES.length);
  const image = DEFAULT_FALLBACK_IMAGES[index];
  return image ? image : DEFAULT_FALLBACK_IMAGES[0]!;
}

export function LocationBasedCityscapeBackground() {
  const [cityscapeUrl, setCityscapeUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageReady, setImageReady] = useState(false);
  const hasLoaded = useRef(false);

  useEffect(() => {
    // Prevent multiple calls in React strict mode
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    async function loadCityscape() {
      try {
        const userLocation = await getUserGeolocation();

        let imageUrl: string | null = null;

        if (userLocation && userLocation.lat && userLocation.lng) {
          imageUrl = await getCityscapeForLocation(userLocation.lat, userLocation.lng);
        } else {
          const { getRandomCityscapeUrl } = await import("@/app/actions/s3-cityscape");
          imageUrl = await getRandomCityscapeUrl();
        }

        setCityscapeUrl(imageUrl || getRandomFallbackImage());
      } catch (error) {
        // Silent fallback - no console errors
        setCityscapeUrl(getRandomFallbackImage());
      } finally {
        setIsLoading(false);
      }
    }

    loadCityscape();
  }, []);

  return (
    <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none select-none">
      {/* Dark atmospheric base — matches cityscape aesthetic, no white flash */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />

      {/* Loading shimmer overlay — subtle pulse while geolocation resolves */}
      {isLoading && (
        <div className="absolute inset-0 animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-700/20 to-transparent" />
        </div>
      )}

      {/* Cityscape image — fades in smoothly once loaded */}
      {cityscapeUrl && (
        <Image
          src={cityscapeUrl}
          alt="Cityscape Background"
          fill
          className={`object-cover object-center opacity-90 transition-opacity duration-700 ease-out ${
            imageReady ? "opacity-90" : "opacity-0"
          }`}
          priority
          quality={100}
          unoptimized
          onLoad={() => setImageReady(true)}
          onError={() => {
            // If S3 cityscape failed, try fallback once
            setCityscapeUrl((prev) => {
              if (prev && !prev.includes('unsplash.com')) {
                console.warn('[CITYSCAPE] S3 image failed to load, falling back to Unsplash');
                return getRandomFallbackImage();
              }
              return prev;
            });
          }}
        />
      )}
    </div>
  );
}
